import React from 'react';
import s from '../../assets/common/CommonStyle.module.css';
import UserInfo from '../../Components/User/UserInfo';
import QualificationChartTeamValue from '../../Components/Charts/QualificationChartTeamValue';
import QualificationChartTeamBeginer from '../../Components/Charts/QualificationChartTeamBeginer';
import TopTitleBlock from '../../Components/TopTitleBlock/TopTitleBlock';
import AchieveRecommend from './components/AchieveRecommend/AchieveRecommend';
import VolumeCommand from './components/VolumeCommand';
import {useTranslation} from 'react-i18next';
import ActivityTreeTable from './components/ActivityTreeTable';
import {
	useGetQlfcLevelQuery,
	useGetUserActivityTreeQuery,
} from '../../redux/userClientsApi';
import QlfcLevelsTable from './components/QlfcLevelsTable';
import {monthNow} from '../../assets/utils/dateUtils';
import PreLoaderComponent from '../../assets/Animations/PreLoaderComponent/PreLoaderComponent';
import LidersStat from './components/LidersStat';
import {useGetAchieveRecommendQuery} from '../../redux/myDynamicApi';
import HistoryMarketplan from './components/HistoryMarketplan';
import HistoryQlfc from './components/HistoryQlfc';
import {useStatusRestriction} from '../../hooks/useStatusRestriction';
import StatusRestrictionMessage from '../../Components/StatusRestrictionMessage/StatusRestrictionMessage';

const PersonalDynamic: React.FC = () => {
	const {t, i18n} = useTranslation();
	const restrictions = useStatusRestriction();

	const [currentMonth, setCurrentMonth] = React.useState<string>(monthNow());

	const {data: archiveRecommended} = useGetAchieveRecommendQuery(undefined, {
		skip: !restrictions.canShowDynamicRecommendations,
	});
	const {data: clientsTree, isLoading} = useGetUserActivityTreeQuery(
		currentMonth,
		{
			skip: !restrictions.canShowNetworkClients,
		},
	);
	const {data: qlfcLevel, isLoading: isLoadingQlfcLevel} = useGetQlfcLevelQuery(
		currentMonth,
		{
			skip: !restrictions.canShowNetworkClients,
		},
	);

	const activityFirstLevelNodes = React.useMemo(() => {
		const source = Array.isArray(clientsTree?.activity)
			? clientsTree?.activity
			: clientsTree?.activity
				? [clientsTree.activity]
				: [];
		return source?.reduce((acc: any[], a: any) => {
			if (a && Array.isArray(a.children)) acc = acc.concat(a.children);
			return acc;
		}, []);
	}, [clientsTree]);

	if (!restrictions.canAccessDynamicPage) {
		return (
			<div className="page__content">
				<StatusRestrictionMessage message={t('Сторінка недоступна')} />
			</div>
		);
	}

	const formatMonthLabel = (value: string) => {
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) {
			return value;
		}
		const lang = i18n.language || 'uk';
		const locale = lang.startsWith('uk') ? 'uk-UA' : 'en-US';
		const formatted = new Intl.DateTimeFormat(locale, {
			month: 'long',
			year: 'numeric',
		}).format(date);
		return formatted.charAt(0).toUpperCase() + formatted.slice(1);
	};

	return (
		<div className="page__content">
			<TopTitleBlock title={t('Моя динаміка')} />
			<div className="page__user user">
				{archiveRecommended ? (
					<div className="user__row">
						<div className="user__column">
							<UserInfo />
						</div>
						<div className="user__column">
							<AchieveRecommend data={archiveRecommended} />
						</div>
					</div>
				) : (
					<div className="user__column">
						<UserInfo />
					</div>
				)}
			</div>
			{restrictions.canShowDynamicGraphs && (
				<VolumeCommand month={currentMonth} onMonthChange={setCurrentMonth} />
			)}
			{restrictions.canShowDynamicGraphs && (
				<div className="page__volume volume">
					<div className="volume__row">
						<div className="volume__column">
							<div className="volume__title">{t('Командний ТО')}</div>
							<div className="volume__chart-left left-chart">
								<QualificationChartTeamValue />
							</div>
						</div>
						<div className="volume__column">
							<div className="volume__top-right">
								<div className="volume__title ">{t('Команда (новачки)')}</div>
								<div className="volume__stats">
									<div className="volume__stat volume__stat-deleted">
										{t('видалено')}
									</div>
									<div className="volume__stat">{t('зареєстровано')}</div>
								</div>
							</div>
							<div className="volume__chart-right right-chart">
								<QualificationChartTeamBeginer />
							</div>
						</div>
					</div>
				</div>
			)}
			{restrictions.canShowDynamicLiders && <LidersStat />}

			{!restrictions.canShowNetworkClients &&
				!restrictions.canShowDynamicHistory && (
					<StatusRestrictionMessage
						message={t(
							"Тут з'явиться інформація коли ви зареєструєте клієнтів в свою мережу.",
						)}
					/>
				)}

			{restrictions.canShowDynamicHistory && (
				<div className="page__history history">
					<div className="history__row">
						<HistoryMarketplan />
						<HistoryQlfc />
					</div>
				</div>
			)}
			{restrictions.canShowNetworkClients && isLoading && (
				<PreLoaderComponent type={'category'} size={'compact'} />
			)}
			{restrictions.canShowNetworkClients &&
				!isLoading &&
				activityFirstLevelNodes &&
				activityFirstLevelNodes.length > 0 && (
					<div className="history__column">
						<div className="history__title name">
							{t('Мережа клієнтів')} {formatMonthLabel(currentMonth)}
						</div>
						<ActivityTreeTable nodes={activityFirstLevelNodes} />
					</div>
				)}

			{restrictions.canShowNetworkClients && isLoadingQlfcLevel && (
				<PreLoaderComponent type={'category'} size={'compact'} />
			)}
			{restrictions.canShowNetworkClients &&
				!isLoadingQlfcLevel &&
				qlfcLevel && (
					<div className={s.tableContainer}>
						<div className="history__column qlfcTable">
							<div className="history__title name">
								{t('Розподіл кваліфікацій за рівнями')}{' '}
								{formatMonthLabel(currentMonth)}
							</div>
							<QlfcLevelsTable data={qlfcLevel} month={currentMonth} />
						</div>
					</div>
				)}
		</div>
	);
};

export default PersonalDynamic;
